import ReviewTable from '@/components/Studies/Create/Steps/Groups/ReviewTable';
import Button from '@/components/UI/Button';
import { _isNil, uuid } from '@/littledash';
import type { DisplayedGroupMetadata } from '@/model/Metadata.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { modalAction } from '@/utils/modal';
import { RiAlertFill } from 'react-icons/ri';
import { useStudyCreateGroups } from '@/utils/studyCreation/useStudyCreate.tsx';

interface UpdatedTreatmentGroup extends Partial<TreatmentGroup> {
  tempId?: string;
}

interface StudyGroupSetupProps {
  groups: Array<TreatmentGroup>;
  onUpdateGroupData: (groups: Array<UpdatedTreatmentGroup>) => void;
  canEdit?: boolean;
  addTempId?: boolean;
  conflicts?: number;
  savedGroupData: Array<DisplayedGroupMetadata>;
}

const StudyGroupSetup: React.FC<StudyGroupSetupProps> = ({
  groups,
  onUpdateGroupData,
  canEdit = true,
  addTempId = false,
  conflicts = 0,
  savedGroupData,
}) => {
  const { openModal } = modalAction(useDispatch());
  const { plannedAnimalData } = useStudyCreateGroups();
  const [plannedAnimalIssues, setPlannedAnimalIssues] = useState<boolean>(false);
  const onAddGroups = (data: Array<any>) => {
    const updatedStudyGroups = data.map((rowData, rowIndex) => {
      const updatedGroup: UpdatedTreatmentGroup = {
        id: rowData?.id ?? '',
        name: rowData?.name,
        no: rowIndex + 1,
        max_subjects: rowData?.max_subjects,
        color: rowData?.color,
        metadata: rowData?.metadata ?? [],
        created_at: rowData?.created_at,
        updated_at: rowData?.updated_at,
      };
      if (addTempId) {
        updatedGroup['temp_id'] = rowData?.temp_id ?? uuid();
      }
      return updatedGroup;
    });
    onUpdateGroupData(updatedStudyGroups);
    if (plannedAnimalData.type !== 'skip' && plannedAnimalData?.animals.length > 0) {
      const groupsChanged =
        groups.length !== updatedStudyGroups.length ||
        updatedStudyGroups.some((group) => {
          const originalGroup = groups?.find((g) => g.temp_id === group.temp_id);
          return _isNil(originalGroup) || originalGroup?.max_subjects !== group.max_subjects;
        });

      setPlannedAnimalIssues(groupsChanged);
    } else {
      setPlannedAnimalIssues(false);
    }
  };

  return (
    <div data-testid="study-groups-container" className="w-100" data-test-component="StudyGroupSetup">
      {groups.length > 0 ? (
        <div className="w-100">
          {canEdit && (
            <div className="pv3 ph4 flex justify-end items-center items-end bb b--moon-gray">
              {conflicts > 0 && (
                <div className="mr3 flex items-center">
                  <RiAlertFill className="red" size={25} />
                  <span className="pl2">Amend groups to proceed</span>
                </div>
              )}
              {plannedAnimalIssues && (
                <div className="mr3 flex items-center">
                  <RiAlertFill className="yellow" size={25} />
                  <span className="pl2">Group size doesn’t match the animal list in this study</span>
                </div>
              )}
              <Button
                plain
                onClick={() =>
                  openModal('CREATE_TREATMENT_GROUPS', {
                    groups,
                    onAddGroups,
                    savedGroupData,
                  })
                }
                testId="group-edit-button"
              >
                Edit
              </Button>
            </div>
          )}
          <ReviewTable groups={groups} groupMetadata={savedGroupData} />
        </div>
      ) : (
        <div
          className="br2 bg-near-white tc flex items-center justify-center flex-column pa4"
          data-test-component="StudyGroupSetup"
          data-test-element="empty-state"
        >
          <div className="mw6 center ph4 pa4">
            <h3 className="mb2">Adding treatment groups</h3>
            <p className="mb3">A preview of your treatment groups and any associated metadata will appear here</p>
            {canEdit && (
              <Button
                stateless
                className="basier-reg blue ph0 mt2"
                testId="add-treatment-groups-button"
                onClick={() =>
                  openModal('CREATE_TREATMENT_GROUPS', {
                    groups,
                    onAddGroups,
                  })
                }
              >
                Add treatment groups
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
export default StudyGroupSetup;
